import type { PagedResponse } from '../types/api'
import type { AuditLogSummary, AuditLogDetail, AuditLogQuery, AuditCategory } from '../types/auditLog'
import { createHeaders } from './authHeaders'
import { fetchApi } from './api'
import { ApiError } from './apiError'

const AUDIT_BASE = '/api/v1/audit'

// JSON calls go through the shared fetchApi, so a failure here is the same typed ApiError — with its
// status — as everywhere else in the app. This module used to carry a private copy that threw a bare
// Error, which left the audit page unable to tell a 403 from a 500.

function buildQueryString(query: AuditLogQuery): string {
  const params = new URLSearchParams()
  if (query.entityType) params.set('entityType', query.entityType)
  if (query.entityId) params.set('entityId', query.entityId)
  if (query.userId) params.set('userId', query.userId)
  if (query.operation) params.set('operation', query.operation)
  if (query.category) params.set('category', query.category)
  if (query.from) params.set('from', query.from)
  if (query.to) params.set('to', query.to)
  if (query.skip != null) params.set('skip', query.skip.toString())
  if (query.top != null) params.set('top', query.top.toString())
  return params.toString()
}

export async function getAuditLog(query: AuditLogQuery): Promise<PagedResponse<AuditLogSummary>> {
  const qs = buildQueryString(query)
  return fetchApi<PagedResponse<AuditLogSummary>>(`${AUDIT_BASE}${qs ? `?${qs}` : ''}`)
}

export async function getAuditLogDetail(id: string): Promise<AuditLogDetail> {
  return fetchApi<AuditLogDetail>(`${AUDIT_BASE}/${encodeURIComponent(id)}`)
}

/**
 * The named operation groups the API filters on.
 *
 * Fetched rather than hardcoded. Which operations constitute a permission change
 * is a decision owned by the server, and a copy of that list in this file would
 * rot silently — a ninth governance operation would simply stop being reported,
 * with the page still rendering and still looking complete.
 */
export async function getAuditCategories(): Promise<AuditCategory[]> {
  return fetchApi<AuditCategory[]>(`${AUDIT_BASE}/categories`)
}

/**
 * A failed export as the same typed error the JSON calls throw.
 *
 * The export cannot use fetchApi — its success is a blob, not JSON — but its failure is an ordinary API
 * failure and must carry its status like one. The message is chosen as fetchApi chooses it: a `message`
 * field, then the ProblemDetails `detail` (the actionable part: which category was unknown), then its
 * `title`, then the body's text, then a fallback by status.
 */
async function exportFailure(response: Response): Promise<ApiError> {
  const raw = await response.text().catch(() => '')
  let body: unknown = raw ? raw : null
  if (raw) {
    try {
      body = JSON.parse(raw)
    } catch {
      // Not JSON: the text itself is the body.
    }
  }

  const record =
    typeof body === 'object' && body !== null && !Array.isArray(body) ? (body as Record<string, unknown>) : null
  const field = (key: string) => (typeof record?.[key] === 'string' ? (record[key] as string) : null)
  const title = field('title')
  const detail = field('detail')

  const fallback =
    response.status === 401
      ? 'Unauthorized - please sign in or check your API key'
      : response.status === 403
        ? 'Forbidden - you do not have access to this resource'
        : `Export failed: ${response.status} ${response.statusText}`.trim()
  const text = typeof body === 'string' && body ? body : null
  const message = field('message') ?? detail ?? title ?? text ?? fallback

  return new ApiError(response.status, message, body, title, detail)
}

export async function exportAuditLog(query: AuditLogQuery): Promise<{ blob: Blob; filename: string }> {
  // Export ignores paging; only the filters apply.
  const qs = buildQueryString({
    entityType: query.entityType,
    // The entity id goes too: one report's access history exported without it is the whole trail
    // under that report's name.
    entityId: query.entityId,
    userId: query.userId,
    operation: query.operation,
    // The category goes too. A spreadsheet named for the filtered view and filled
    // with the unfiltered trail outlives the screen that explained what it was.
    category: query.category,
    from: query.from,
    to: query.to,
  })

  const headers = await createHeaders()
  // Remove Content-Type for blob download
  delete (headers as Record<string, string>)['Content-Type']

  const response = await fetch(`${AUDIT_BASE}/export${qs ? `?${qs}` : ''}`, {
    headers,
    // 'omit' for the reason spelled out in services/api.ts fetchApi: auth is a header, and on
    // localhost cookies are shared across ports.
    credentials: 'omit',
  })

  if (!response.ok) {
    throw await exportFailure(response)
  }

  const blob = await response.blob()
  const disposition = response.headers.get('content-disposition')
  const filename = disposition?.match(/filename=(.+)/)?.[1] ?? 'AuditLog.xlsx'

  return { blob, filename }
}
